import { useCallback, useMemo, useRef, useState } from 'react';
const DEFAULT_PAGE_SIZE = 50;
export function usePaginatedImages(images, options) {
  if (options === void 0) {
    options = {};
  }
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    formatItemsRange,
    compactPanelLayout,
    isEditor3,
    contentRef,
    onPageChange,
    onPaginationClick
  } = options;
  const effectiveCompactLayout = compactPanelLayout ?? isEditor3 ?? false;
  const [currentPage, setCurrentPage] = useState(0);
  const contentRefRef = useRef(contentRef);
  contentRefRef.current = contentRef;
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  const onPaginationClickRef = useRef(onPaginationClick);
  onPaginationClickRef.current = onPaginationClick;
  const totalPages = Math.ceil(images.length / pageSize);
  const safeCurrentPage = Math.min(currentPage, Math.max(0, totalPages - 1));
  if (safeCurrentPage !== currentPage) {
    setCurrentPage(safeCurrentPage);
  }
  const paginatedImages = useMemo(() => images.slice(safeCurrentPage * pageSize, (safeCurrentPage + 1) * pageSize), [images, safeCurrentPage, pageSize]);
  const scrollToTop = useCallback(() => {
    var _contentRefRef$curren;
    (_contentRefRef$curren = contentRefRef.current) == null || (_contentRefRef$curren = _contentRefRef$curren.current) == null || _contentRefRef$curren.scrollTo({
      top: 0
    });
  }, []);
  const onClickNext = useCallback(isTopPagination => {
    setCurrentPage(prev => {
      const next = Math.min(prev + 1, totalPages - 1);
      if (next !== prev) {
        scrollToTop();
        onPageChangeRef.current == null || onPageChangeRef.current(next);
      }
      return next;
    });
    onPaginationClickRef.current == null || onPaginationClickRef.current({
      direction: 'next',
      isTopPagination
    });
  }, [totalPages, scrollToTop]);
  const onClickBack = useCallback(isTopPagination => {
    setCurrentPage(prev => {
      const next = Math.max(prev - 1, 0);
      if (next !== prev) {
        scrollToTop();
        onPageChangeRef.current == null || onPageChangeRef.current(next);
      }
      return next;
    });
    onPaginationClickRef.current == null || onPaginationClickRef.current({
      direction: 'back',
      isTopPagination
    });
  }, [scrollToTop]);
  const resetPagination = useCallback(() => {
    setCurrentPage(0);
  }, []);
  const paginationConfig = useMemo(() => totalPages > 1 ? {
    compactPanelLayout: effectiveCompactLayout,
    currentPage: safeCurrentPage,
    formatItemsRange,
    isEditor3: effectiveCompactLayout,
    onClickBack,
    onClickNext,
    pageSize,
    totalItems: images.length,
    totalPages
  } : undefined, [safeCurrentPage, effectiveCompactLayout, formatItemsRange, onClickBack, onClickNext, pageSize, images.length, totalPages]);
  return {
    currentPage: safeCurrentPage,
    paginatedImages,
    paginationConfig,
    resetPagination,
    setCurrentPage,
    totalPages
  };
}
//# sourceMappingURL=use-paginated-images.js.map